import type { Ayah } from '@app-types/KuranTypes'

/**
 * The Quran page files (`data/quran/*.json`) include the Diyanet tefsir so the
 * mobile app can show it offline (see `scripts/merge-tefsir-into-quran-pages.js`).
 * Browsers should not download it with every page, so the web strips it and
 * fetches tefsir per ayah only when the reader opens it.
 */

/** Bundled tefsir of one ayah, ready to show. */
export type ResolvedBundledTefsir = {
  range: string
  html: string
}

/** Returns the ayahs without the bundled tefsir, for pages sent to browsers. */
export const removeBundledTefsir = (ayahs: Ayah[]): Ayah[] =>
  ayahs.map(({ diyanet_tefsir: _bundledTefsir, ...ayah }) => ayah)

/**
 * Finds an ayah's bundled tefsir on its page. The text is stored only on the
 * first ayah of its range, so other ayahs of the range look it up there.
 * Returns null when the page has no bundled tefsir (web, older app data).
 */
export const getBundledAyahTefsir = (
  ayah: Ayah,
  pageAyahs: Ayah[],
): ResolvedBundledTefsir | null => {
  const range = ayah.diyanet_tefsir?.range
  if (!range) return null

  const ayahWithText = pageAyahs.find(
    (pageAyah) =>
      pageAyah.surah.id === ayah.surah.id &&
      pageAyah.diyanet_tefsir?.range === range &&
      pageAyah.diyanet_tefsir.html !== undefined,
  )
  const html = ayahWithText?.diyanet_tefsir?.html
  // An empty string is kept: the source has no tefsir for that verse (e.g. 46:1).
  return html !== undefined ? { range, html } : null
}
